import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { liveChallengeActivationsApi } from '@/services/liveChallenges';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useConfirm } from '@/providers/ConfirmProvider';

// Cores funcionais das alternativas (estilo Kahoot) — não temas de marca,
// é correspondência visual com o app do aluno.
const ALTERNATIVE_COLORS = ['bg-danger', 'bg-primary', 'bg-warning', 'bg-success'];

export default function QuestionView({ challenge, activationId, onPhaseChange }) {
  const { currentQuestion, timeRemaining, timerSeconds, isTimerRunning, answerCount, participantCount, questionIndex, totalQuestions, questionRevealed, startTimer } = useLiveChallenge();
  const { confirm } = useConfirm();
  const [localQuestion, setLocalQuestion] = useState(null);
  const [revealing, setRevealing] = useState(false);

  // Limpa localQuestion quando a Q muda (broadcast question_revealed da próxima)
  // ou quando o estado oficial sinaliza não-revelada (question_closed). Sem isso,
  // o stale local cobria as transições e mantinha isRevealed=true em cenários
  // onde o admin deveria ver o preview da próxima Q.
  useEffect(() => {
    if (localQuestion && (currentQuestion?.id !== localQuestion.id || !questionRevealed)) {
      setLocalQuestion(null);
    }
  }, [currentQuestion?.id, questionRevealed, localQuestion]);

  const activeQuestion = currentQuestion || localQuestion;
  // Após F5 entre questões, o backend devolve current_question populado (a próxima
  // a ser revelada) mas questionRevealed=false. localQuestion cobre o gap entre o
  // clique em "Revelar questão" e a chegada do broadcast question_revealed.
  const isRevealed = questionRevealed || !!localQuestion;
  const alternatives = activeQuestion?.['live-challenge-alternatives'] || activeQuestion?.live_challenge_alternatives || activeQuestion?.alternatives || [];
  // Timer config do desafio vem do LiveChallengeActivationSerializer (timer-seconds).
  // A response de revealQuestion é o QuestionSerializer e NÃO inclui esse campo,
  // então usar question.timer_seconds caía sempre no fallback 30.
  const challengeTimer = challenge?.timer_seconds ?? 30;
  // ?? em vez de || para que 0 (timer encerrado) não caia em fallback maior,
  // o que fazia o display "voltar pra 30" quando o tempo zerava.
  const activeTimer = timeRemaining ?? 0;
  const activeTimerMax = timerSeconds || challengeTimer;
  const progress = activeTimerMax > 0 ? (activeTimer / activeTimerMax) * 100 : 0;
  // Total esperado é a quantidade de participantes; answerCount.total só é
  // populado pelo backend após a 1ª resposta (ficava "0 de 0" antes disso).
  const answeredRatio = participantCount > 0 ? Math.round((answerCount.answered / participantCount) * 100) : 0;

  const handleRevealQuestion = async () => {
    setRevealing(true);
    try {
      const response = await liveChallengeActivationsApi.revealQuestion(activationId);
      const raw = response?.data;
      const question = raw?.attributes ? { id: raw.id, ...Object.fromEntries(Object.entries(raw.attributes).map(([k, v]) => [k.replace(/-/g, '_'), v])) } : raw;
      setLocalQuestion(question);
      startTimer(challengeTimer);
    } catch {
      toast.error('Erro ao revelar questão.');
    } finally {
      setRevealing(false);
    }
  };

  const handleCloseQuestion = async () => {
    try {
      await liveChallengeActivationsApi.closeQuestion(activationId);
      if (onPhaseChange) onPhaseChange('questionResult');
    } catch {
      toast.error('Erro ao encerrar questão.');
    }
  };

  if (!activeQuestion || !isRevealed) {
    // Sincronizar com o backend: questionIndex do provider (vindo de current-question-index)
    // é a fonte canônica após F5; o challenge prop pode estar stale se carregou antes do
    // useEffect de fetch. Falha pra current_question_index do prop em último caso.
    const currentIdx = (questionIndex ?? challenge?.current_question_index) || 0;
    // Preview completo (destaque da correta + lista de próximas) só existe se o host é DONO
    // do template — live_challenge_questions só vem preenchido nesse caso (gate no backend,
    // ver ControlPanel/index.jsx). Host público não-dono: allQuestions fica [] e caímos no
    // current_question da própria ativação (mesma próxima questão, sem is_correct/lista).
    const allQuestions = challenge?.live_challenge_questions || [];
    const templateQuestion = allQuestions[currentIdx];
    const nextQuestion = templateQuestion || challenge?.current_question;
    const nextAlternatives = templateQuestion ? templateQuestion.live_challenge_alternatives || [] : nextQuestion?.['live-challenge-alternatives'] || nextQuestion?.live_challenge_alternatives || [];
    const upcoming = allQuestions.length > 0 ? allQuestions.slice(currentIdx + 1) : [];
    const handleBackToLobby = async () => {
      const ok = await confirm({
        title: 'Voltar para o lobby?',
        description: 'O desafio será reiniciado do zero. Todos os participantes serão removidos e qualquer pontuação acumulada será perdida.',
        confirmText: 'Sim, recomeçar',
        cancelText: 'Cancelar',
        variant: 'destructive'
      });
      if (!ok) return;

      try {
        await liveChallengeActivationsApi.backToLobby(activationId);
        toast.success('Desafio reiniciado. Aguardando participantes no lobby.');
        if (onPhaseChange) onPhaseChange('lobby');
      } catch (err) {
        const message = err?.response?.data?.error || err?.response?.data?.message || 'Erro ao voltar para o lobby.';
        toast.error(message);
      }
    };

    return (
      <div className="grid grid-cols-1 gap-8 py-8 md:grid-cols-2 md:gap-12">
        {/* Coluna 1: preview da próxima questão */}
        <div className="flex flex-col gap-4">
          <header>
            <h2 className="m-0 text-xl font-bold text-foreground">Próxima questão</h2>
            <p className="mt-1 text-sm text-neutral">Revise antes de revelar aos participantes.</p>
          </header>

          {nextQuestion ? (
            <Card
              border
              orientation="vertical"
              className="px-5"
            >
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                  Questão {currentIdx + 1} de {allQuestions.length || challenge?.total_questions || '?'}
                </span>
              </div>
              <p className="mb-4 text-base font-semibold text-neutral-dark">{nextQuestion.description}</p>
              <ul className="m-0 flex list-none flex-col gap-2 pl-0">
                {nextAlternatives.map((alt, idx) => (
                  <li
                    key={alt.id || idx}
                    className={`flex items-start gap-2 rounded-md border border-solid px-3 py-2 text-sm ${alt.is_correct ? 'border-success/40 bg-success-lightest text-success' : 'border-neutral-lightest text-neutral-dark'}`}
                  >
                    <span className="font-bold">{String.fromCharCode(65 + idx)}.</span>
                    <span>{alt.description}</span>
                    {alt.is_correct && <span className="ml-auto text-xs font-semibold uppercase tracking-wide">correta</span>}
                  </li>
                ))}
              </ul>
            </Card>
          ) : (
            <p className="m-0 text-sm text-neutral-light">Nenhuma questão configurada.</p>
          )}
        </div>

        {/* Coluna 2: ações + lista de subsequentes */}
        <div className="flex flex-col gap-6">
          <Card
            border
            orientation="vertical"
            className="px-5"
          >
            <h3 className="m-0 mb-2 text-base font-semibold text-foreground">Desafio iniciado</h3>
            <p className="mb-4 text-sm text-neutral">Quando estiver pronto, libere a questão acima para os participantes responderem.</p>
            <div className="flex flex-col gap-2">
              <Button
                type="button"
                variant="default"
                size="lg"
                fullWidth
                disabled={revealing || !nextQuestion}
                onClick={handleRevealQuestion}
              >
                {revealing ? 'Revelando...' : 'Revelar questão'}
              </Button>
              <Button
                type="button"
                variant="outline-danger"
                size="md"
                fullWidth
                onClick={handleBackToLobby}
              >
                Reiniciar e voltar para o lobby
              </Button>
            </div>
          </Card>

          {upcoming.length > 0 && (
            <div className="flex flex-col gap-2">
              <h3 className="m-0 text-base font-semibold text-foreground">Próximas questões</h3>
              <Card
                border
                orientation="vertical"
                className="px-0 py-0"
              >
                <ol className="m-0 list-none pl-0">
                  {upcoming.map((q, idx) => (
                    <li
                      key={q.id || idx}
                      className="flex items-center gap-3 border-b border-solid border-neutral-lightest px-4 py-3 text-sm last:border-0"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-lightest text-xs font-bold text-primary-dark">{currentIdx + idx + 2}</span>
                      <span className="line-clamp-2 text-neutral-dark">{q.description || `Questão ${currentIdx + idx + 2}`}</span>
                    </li>
                  ))}
                </ol>
              </Card>
            </div>
          )}
        </div>
      </div>
    );
  }

  const isUrgent = activeTimer <= 5;

  return (
    <div className="grid grid-cols-1 gap-8 py-8 md:grid-cols-2 md:gap-12">
      {/* Coluna 1: Questão e alternativas (o que o aluno vê) */}
      <div className="flex flex-col gap-6">
        <p className="m-0 text-sm font-semibold text-neutral">
          Questão {(questionIndex || 0) + 1} de {totalQuestions || '?'}
        </p>

        <Card
          border
          orientation="vertical"
          className="px-6 py-6 text-center"
        >
          <h2 className="m-0 text-xl font-bold text-foreground">{activeQuestion?.description || 'Carregando questão...'}</h2>
        </Card>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {alternatives.map((alt, idx) => (
            <div
              key={alt.id || idx}
              className={`flex items-center justify-center rounded-lg p-5 text-center text-base font-bold text-white shadow-dropshadow-md ${ALTERNATIVE_COLORS[idx % ALTERNATIVE_COLORS.length]}`}
            >
              <span className="mr-2 text-lg opacity-80">{String.fromCharCode(65 + idx)}</span>
              {alt.description}
            </div>
          ))}
        </div>
      </div>

      {/* Coluna 2: Timer + progresso + ação (controle do professor) */}
      <div className="flex flex-col items-center gap-8">
        <div className="flex flex-col items-center gap-2">
          <div className="relative flex h-32 w-32 items-center justify-center">
            <svg
              className="h-32 w-32 -rotate-90"
              viewBox="0 0 100 100"
            >
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                className="text-neutral-lightest"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                strokeDasharray={`${2 * Math.PI * 45}`}
                strokeDashoffset={`${2 * Math.PI * 45 * (1 - progress / 100)}`}
                strokeLinecap="round"
                className={`transition-all duration-1000 ease-linear ${isUrgent ? 'text-danger' : 'text-primary'}`}
              />
            </svg>
            <span className="absolute text-3xl font-bold text-foreground">{activeTimer}</span>
          </div>
          {isTimerRunning && <p className="m-0 text-xs text-neutral-light">segundos restantes</p>}
        </div>

        <Card
          border
          orientation="vertical"
          className="w-full px-5"
        >
          <div className="mb-2 flex justify-between text-sm text-neutral-dark">
            <span className="font-semibold">Respostas recebidas</span>
            <span className="font-bold text-primary">{answeredRatio}%</span>
          </div>
          <div className="mb-2 text-xs text-neutral">
            {answerCount.answered} de {participantCount} participantes
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-neutral-lightest">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300 ease-out-strong"
              style={{ width: `${answeredRatio}%` }}
            />
          </div>
        </Card>

        <Button
          type="button"
          variant="destructive"
          size="lg"
          fullWidth
          onClick={handleCloseQuestion}
        >
          Encerrar Questão
        </Button>
      </div>
    </div>
  );
}
